import styled from 'styled-components';
import { baseVariables } from '@marriott/mi-ui-library';

// Constants
const borderRadius = 16;
const aspectRatios = {
  tall: '2 / 3',
  wide: '16 / 9',
  square: '1 / 1',
};

// Base styles
export const StyledProductDetailsCard = styled.div<{ cardVariation: string }>`
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: ${borderRadius}px;
  background: #fff;
  overflow: hidden;
  display: flex;
  flex-direction: ${({ cardVariation }) => (cardVariation === 'wide' ? 'row' : 'column')};
  width: 100%;
  transition: box-shadow ${baseVariables.animations.durationExtended};
  position: relative;
  aspect-ratio: ${aspectRatios.square};
  height: auto;

  // Aspect ratio styles
  &.pdc-tall {
    @media ${baseVariables.mediaQuery.xl} {
      aspect-ratio: ${aspectRatios.tall};
    }
  }
  &.pdc-wide {
    width: max-content;
    @media ${baseVariables.mediaQuery.xl} {
      aspect-ratio: ${aspectRatios.wide};
    }
  }

  .pdc-card {
    position: relative;
    z-index: 1;
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: ${borderRadius}px;
      background: linear-gradient(0deg, rgba(28, 28, 28, 0.8) 0%, rgba(28, 28, 28, 0.8) 100%);
      opacity: 0;
      transition: opacity ${baseVariables.animations.durationStandard};
      z-index: 2;
      pointer-events: none;
    }

    @media ${baseVariables.mediaQuery.xl} {
      &:hover::after,
      &:focus-within::after,
      &:focus-visible::after {
        opacity: 0.8;
      }
      &:hover .pdc-hovercontent,
      &:focus-visible .pdc-hovercontent,
      &:focus-within .pdc-hovercontent {
        display: block;
      }

      &:hover .pdc-brand-logo::before,
      &:focus-visible .pdc-brand-logo::before,
      &:focus-within .pdc-brand-logo::before,
      &:hover .pdc-content::before,
      &:focus-visible .pdc-content::before,
      &:focus-within .pdc-content::before {
        transition: opacity 200ms ease-in 50ms;
        padding-bottom: 0;
        opacity: 0;
      }
    }
    &.is-expanded {
      &::after {
        opacity: 0.8;
      }
      .pdc-hovercontent {
        display: block;
      }
      .pdc-brand-logo::before,
      .pdc-content::before {
        transition: opacity 200ms ease-in 50ms;
        padding-bottom: 0;
        opacity: 0;
      }
    }
  }

  // Image styles
  picture {
    width: 100%;
    aspect-ratio: inherit;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    aspect-ratio: ${aspectRatios.square};
  }
  &.pdc-wide picture {
    @media ${baseVariables.mediaQuery.xl} {
      aspect-ratio: ${aspectRatios.wide};
    }
  }
  &.pdc-tall picture {
    @media ${baseVariables.mediaQuery.xl} {
      aspect-ratio: ${aspectRatios.tall};
    }
  }
  .pdc-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: ${borderRadius}px ${borderRadius}px 0 0;
    display: block;
  }

  // Content styles
  .pdc-hovercontent {
    display: none;
  }
  .pdc-content {
    position: absolute;
    inset: 0;
    z-index: 3;
    display: flex;
    flex-direction: column;
    border-radius: ${borderRadius}px;
    pointer-events: none;
    padding: 24px;
    &::before {
      content: '';
      position: absolute;
      inset: 0;
      border-radius: ${borderRadius}px;
      height: 164px;
      z-index: 1;
      opacity: 1;
      transition: opacity 200ms ease-in 100ms;
      background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0.62) 0.08%,
        rgba(0, 0, 0, 0.54) 62.91%,
        rgba(0, 0, 0, 0) 95.83%
      );
    }
  }
  .pdc-content-top {
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 2;
  }
  .pdc-content-bottom {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-top: auto;
  }

  // Brand logo styles
  .pdc-brand-logo {
    &::before {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 160%;
      height: 80%;
      pointer-events: none;
      z-index: 1;
      background: radial-gradient(109.43% 141.42% at 0% 100%, rgba(0, 0, 0, 0.54) 20%, rgba(0, 0, 0, 0) 40%);
    }
    > span {
      position: relative;
      z-index: 2;
    }
  }

  // Mobile toggle styles
  .pdc-mobile-toggle {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    background: ${baseVariables.color['base20']};
    border-radius: 50%;
    cursor: pointer;
    pointer-events: auto;
    z-index: 5;
    height: 28px;
    width: 28px;
    @media ${baseVariables.mediaQuery.xl} {
      display: none;
    }
    .icon-plus {
      transition: transform ${baseVariables.animations.durationStandard} ease;
      display: flex;
      align-items: center;
      justify-content: center;
      &::before {
        font-size: 18px;
        line-height: 18px;
        position: relative;
        bottom: 1px;
      }
    }
    &.is-expanded {
      .icon-plus {
        &::before {
          transform: rotate(45deg);
        }
      }
    }
  }
`;

export const StyledEyebrow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  line-height: 12px;
`;

export const StyledTitleRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  justify-content: space-between;
  .icon-arrow-right {
    height: 24px;
    width: 24px;
    font-size: 24px;
    margin-bottom: 4px;
  }
`;

export const StyledTags = styled.ul`
  display: flex;
  gap: 4px;
  padding: 0;
  margin-bottom: 8px;
  list-style: none;
  & li {
    color: ${baseVariables.color['base20']};
    font-size: 12px;
    padding: 0 8px;
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.54);
  }
`;
